<template>
  <div v-title="$route.meta.title">
    <div class="page-heading">
      <h3>{{ $route.meta.title }}</h3>
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>考试培训管理</el-breadcrumb-item>
        <el-breadcrumb-item>{{ $route.meta.title }}</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <layout-panel>
      <template slot="header">搜索</template>
      <el-form
        ref="searchForm"
        :model="search"
        label-width="108px"
        label-position="right"
        size="medium"
        @submit.native.prevent="onSearch()">
        <div class="re-el-form__flex">
          <el-form-item label="考试标题"
                        prop="title">
            <el-input v-model="search.title"
                      clearable
                      placeholder="请输入考试标题"/>
          </el-form-item>
          <el-form-item label="考试类型"
                        prop="open_book_exam">
            <el-select v-model="search.open_book_exam"
                       class="width-max"
                       clearable
                       placeholder="全部">
              <el-option v-for="item in examOptions.openBookMaps"
                         :key="item.value"
                         :label="item.label"
                         :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item label="状态"
                        prop="status">
            <el-select v-model="search.status"
                       class="width-max"
                       clearable
                       multiple
                       placeholder="全部">
              <el-option v-for="item in examOptions.statusMaps"
                         :key="item.value"
                         :label="item.label"
                         :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item label="是否通用"
                        prop="is_common">
            <el-select v-model="search.is_common"
                       class="width-max"
                       clearable
                       placeholder="全部">
              <el-option v-for="item in examOptions.isCommonMaps"
                         :key="item.value"
                         :label="item.label"
                         :value="item.value"/>
            </el-select>
          </el-form-item>
          <el-form-item label="所属知识库"
                        prop="training_knowledge_id">
            <remote-select
              v-model="search.training_knowledge_id"
              class="width-max"
              clearable
              multiple
              keyword-key="name"
              label-key="name"
              value-key="id"
              placeholder="请输入知识库名称"
              :query-request="queryTrainingKnowledge"
              :find-request="getTrainingKnowledge"/>
          </el-form-item>
          <el-form-item label="所属客户"
                        prop="customerid">
            <remote-select
              v-model="search.customerid"
              class="width-max"
              clearable
              multiple
              id-key="number"
              label-key="name"
              value-key="id"
              placeholder="请输入客户名称"
              :query-request="getBasicCustomerTable"
              :find-request="getCustomerDetail"/>
          </el-form-item>
          <el-form-item label="创建人"
                        prop="creator_id">
            <remote-select
              v-model="search.creator_id"
              class="width-max"
              clearable
              multiple
              id-key="id"
              keyword-key="name"
              label-key="name"
              value-key="id"
              placeholder="请输入创建人"
              :query-request="getBasicPlatformUsersIndex"
              :find-request="getPlatformUsersDetail"/>
          </el-form-item>
          <el-form-item label="创建时间"
                        prop="created_at">
            <el-date-picker v-model="search.created_at"
                            style="width: 100%;"
                            type="datetimerange"
                            range-separator="至"
                            start-placeholder="开始日期"
                            end-placeholder="结束日期"
                            align="right"
                            :default-time="['00:00:00', '23:59:59']"
                            value-format="yyyy-MM-dd HH:mm:ss"/>
          </el-form-item>
        </div>
        <el-form-item>
          <el-button type="primary"
                     icon="el-icon-search"
                     native-type="submit">搜索
          </el-button>
          <el-button @click="resetSearch()">重置</el-button>
        </el-form-item>
      </el-form>
    </layout-panel>

    <layout-panel>
      <template slot="header">列表</template>
      <router-link to="/training/intern-examination/create">
        <el-button type="primary"
                   size="small">创建考试
        </el-button>
      </router-link>
      <el-button type="primary"
                 size="small"
                 @click="openInternAssignmentDialog">批量分配实习生
      </el-button>
      <el-table :data="table"
                stripe
                highlight-current-row
                style="width: 100%">
        <el-table-column prop="id"
                         label="考试ID"
                         width="88"/>
        <el-table-column prop="title"
                         label="考试标题"
                         min-width="180">
          <template slot-scope="scope">
            <router-link :to="{ path: `/training/intern-examination/update/${scope.row.id}`, query: { mode: 'view' } }">
              <el-link type="primary">{{ scope.row.title || '-' }}</el-link>
            </router-link>
          </template>
        </el-table-column>
        <el-table-column prop="is_common"
                         label="使用范围"
                         width="100">
          <template slot-scope="scope">
            <span class="course-tag"
                  :class="getTagClass('common', scope.row.intern_scope)">
              {{ getInternScopeLabel(scope.row.intern_scope) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="题目数"
                         width="80">
          <template slot-scope="scope">{{ getQuestionCount(scope.row) }}</template>
        </el-table-column>
        <el-table-column label="及格分"
                         width="90">
          <template slot-scope="scope">{{ formatPassingScore(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="is_common"
                         label="是否通用"
                         width="100">
          <template slot-scope="scope">
            <span class="exam-tag"
                  :class="getTagClass('common', scope.row.is_common)">
              {{ getIsCommonLabel(scope.row.is_common) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="knowledge_name"
                         label="所属知识库"
                         min-width="150">
          <template slot-scope="scope">
            <span class="knowledge-name">
              <i class="el-icon-folder knowledge-name__icon"></i>
              <span>{{ scope.row.knowledge_name }}</span>
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="customer_name"
                         label="所属客户"
                         min-width="140">
          <template slot-scope="scope">{{ scope.row.customer_name || '-' }}</template>
        </el-table-column>
        <el-table-column label="关联答卷数"
                         width="110">
          <template slot-scope="scope">
            <router-link v-if="scope.row.answers_count > 0"
                         :to="getExamRecordLink(scope.row)">
              <span class="exam-paper-count"><i class="el-icon-document"></i>{{ scope.row.answers_count || 0 }}</span>
            </router-link>
            <span v-else class="exam-paper-count">{{ scope.row.answers_count || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="created_at"
                         label="创建时间"
                         width="170"/>
        <el-table-column prop="user_name"
                         label="创建人"
                         min-width="110">
          <template slot-scope="scope">{{ scope.row.user_name || scope.row.creator_name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="status"
                         label="状态"
                         width="100">
          <template slot-scope="scope">
            <span class="exam-tag"
                  :class="getStatusTagClass(scope.row.status)">
              {{ getExamStatusLabel(scope.row.status) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column fixed="right"
                         label="操作"
                         width="250">
          <template slot-scope="scope">
            <router-link :to="`/training/intern-examination/update/${scope.row.id}`">
              <el-button type="primary"
                         size="mini"
                         plain>编辑
              </el-button>
            </router-link>
            <el-button v-if="Number(scope.row.status) === consts.STATUS_NEW"
                       type="success"
                       size="mini"
                       plain
                       @click="handleExamAction(scope.row, 'online')">上架
            </el-button>
            <el-button v-if="Number(scope.row.status) === consts.STATUS_ONLINE"
                       type="warning"
                       size="mini"
                       plain
                       @click="handleExamAction(scope.row, 'offline')">下架
            </el-button>
            <el-button v-if="Number(scope.row.status) === consts.STATUS_OFFLINE && isSuperAdmin"
                       type="danger"
                       size="mini"
                       plain
                       @click="handleExamAction(scope.row, 'ban')">作废
            </el-button>
            <el-button v-if="Number(scope.row.status) === consts.STATUS_OFFLINE"
                       type="success"
                       size="mini"
                       plain
                       @click="handleExamAction(scope.row, 'online')">上架
            </el-button>
            <el-button v-if="Number(scope.row.status) === consts.STATUS_BANNED && isSuperAdmin"
                       type="primary"
                       size="mini"
                       plain
                       @click="handleExamAction(scope.row, 'active')">激活
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
          background
          :current-page="pagination.current_page"
          :page-sizes="[20, 50, 100, 200]"
          :page-size="pagination.per_page"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
          @size-change="handleSizePageChange"
          @current-change="handleCurrentPageChange"/>
      </div>
    </layout-panel>

    <el-dialog
      title="批量分配实习生考试"
      :visible.sync="internAssignmentDialogVisible"
      :close-on-click-modal="!internAssignmentUploading"
      :close-on-press-escape="!internAssignmentUploading"
      @close="resetInternAssignmentDialog">
      <el-form label-width="120px">
        <el-form-item label="分配范围">
          <el-radio-group v-model="internAssignmentMode"
                          :disabled="internAssignmentUploading">
            <el-radio label="all">所有实习生</el-radio>
            <el-radio label="replace">覆盖上传实习生</el-radio>
            <el-radio label="append">追加上传实习生</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="考试文件">
          <el-upload ref="internAssignmentUpload"
                     action=""
                     :auto-upload="false"
                     :limit="1"
                     accept=".xlsx"
                     :disabled="internAssignmentUploading"
                     :on-change="handleInternAssignmentFileChange"
                     :before-upload="beforeInternAssignmentUpload">
            <el-button size="small"
                       :disabled="internAssignmentUploading">选择 Excel 文件
            </el-button>
            <div slot="tip"
                 class="el-upload__tip">仅支持 .xlsx，所有实习生模式实习生账号ID必须留空；覆盖或追加模式实习生账号ID必须填写。
            </div>
          </el-upload>
        </el-form-item>
        <el-form-item><span>{{ internAssignmentModeHint }}</span></el-form-item>
        <el-form-item>
          <el-button type="text"
                     :disabled="internAssignmentUploading"
                     @click="downloadInternAssignmentTemplate">下载模板
          </el-button>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :disabled="internAssignmentUploading"
                   @click="internAssignmentDialogVisible = false">取消</el-button>
        <el-button type="primary"
                   :loading="internAssignmentUploading"
                   @click="submitInternAssignment">开始分配</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import table from '@core/mixins/table'
import trainingMixins from '@views/training/training.mixins'
import { isSuperAdminUser } from '@views/training/training-user'
import {
  consts,
  getExamStatusActions,
  getExamStatusLabel,
  getExamStatusType,
  getOptionLabel,
  opts,
} from '@views/training/training-exam-intern.constants'
import { NumberArray } from '@utils/common'

export default {
  mixins: [table, trainingMixins],
  data () {
    return {
      consts,
      examOptions: opts,
      search: {
        title: null,
        open_book_exam: null,
        target_usertype: null,
        status: [],
        is_common: null,
        training_knowledge_id: [],
        customerid: [],
        creator_id: [],
        created_at: [],
      },
      table: [],
      pagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
      },
      searchFieldType: {
        open_book_exam: Number,
        target_usertype: Number,
        status: NumberArray,
        is_common: Number,
        training_knowledge_id: NumberArray,
        customerid: Array,
        creator_id: NumberArray,
        created_at: Array,
      },
      internAssignmentDialogVisible: false,
      internAssignmentMode: 'all',
      internAssignmentFile: null,
      internAssignmentUploading: false,
    }
  },
  computed: {
    isSuperAdmin () {
      return isSuperAdminUser(this.loginUser)
    },
    internAssignmentModeHint () {
      if (this.internAssignmentMode === 'all') return '所有实习生：账号ID必须留空，按考试ID为所有实习生分配。'
      if (this.internAssignmentMode === 'replace') return '覆盖上传实习生：实习生账号ID必须填写，按考试覆盖实习生分配关系。'
      return '追加上传实习生：实习生账号ID必须填写，在该考试现有实习生分配后追加。'
    },
  },
  methods: {
    ...request.map([
      'queryTrainingKnowledge',
      'getTrainingKnowledge',
      'getBasicCustomerTable',
      'getCustomerDetail',
      'getBasicPlatformUsersIndex',
      'getPlatformUsersDetail',
    ]),
    getTableData (params) {
      return request.page('queryInternTrainingExamination', params)
    },
    openInternAssignmentDialog () {
      this.resetInternAssignmentDialog()
      this.internAssignmentDialogVisible = true
    },
    beforeInternAssignmentUpload (file) {
      if (!/\.xlsx$/i.test(file.name || '')) {
        this.$message.error('仅支持上传 .xlsx 文件')
        return false
      }
      return true
    },
    handleInternAssignmentFileChange (file, fileList) {
      const selected = fileList[fileList.length - 1]
      this.internAssignmentFile = selected && this.beforeInternAssignmentUpload(selected)
        ? (selected.raw || selected)
        : null
    },
    async submitInternAssignment () {
      if (this.internAssignmentUploading) return
      if (!this.internAssignmentFile) {
        this.$message.error('请先选择实习生考试文件')
        return
      }
      this.internAssignmentUploading = true
      try {
        await request.common('uploadInternExaminationAssignment', {
          mode: this.internAssignmentMode,
          file: this.internAssignmentFile,
        })
        this.$message.success('批量分配成功')
        this.internAssignmentDialogVisible = false
        this.internAssignmentUploading = false
        this.resetInternAssignmentDialog()
        this.onRefresh()
      } catch (error) {
      } finally {
        this.internAssignmentUploading = false
      }
    },
    resetInternAssignmentDialog () {
      if (this.internAssignmentUploading) return
      this.internAssignmentMode = 'all'
      this.internAssignmentFile = null
      if (this.$refs.internAssignmentUpload) this.$refs.internAssignmentUpload.clearFiles()
    },
    downloadInternAssignmentTemplate () {
      const row = {
        training_exam_id: '必填',
        intern_user_id: this.internAssignmentMode === 'all' ? '' : '必填',
        name: '选填',
      }
      this.$export('实习生考试批量分配模板', [row], {
        fileHeader: [
          {key: 'training_exam_id', text: '考试ID'},
          {key: 'intern_user_id', text: '实习生账号ID'},
          {key: 'name', text: '姓名'},
        ],
      })
    },
    resetSearch () {
      this.$refs[this.refSearchForm].resetFields()
      this.onSearch()
    },
    getIsCommonLabel (value) {
      return getOptionLabel(this.examOptions.isCommonMaps, value) || '-'
    },
    getInternScopeLabel (value) {
      return getOptionLabel(this.examOptions.InternScopeMaps, value) || '-'
    },
    getExamStatusLabel,
    getStatusTagClass (status) {
      return `exam-tag--${getExamStatusType(status)}`
    },
    getTagClass (type, value) {
      if (type === 'user') {
        return Number(value) === this.consts.TARGETUSER_TYPE_USER
          ? 'exam-tag--primary'
          : 'exam-tag--warning'
      }
      if (type === 'common') {
        return Number(value) === this.consts.IS_COMMON_SHARED
          ? 'exam-tag--success'
          : 'exam-tag--default'
      }
      return 'exam-tag--default'
    },
    getQuestionCount (row) {
      return Array.isArray(row.question_list) ? row.question_list.length : 0
    },
    formatPassingScore (row) {
      if (row.open_book_exam === consts.OPEN_BOOK_EXAM_CLOSED) {
        const passingScore = row.passing_score === undefined || row.passing_score === null ? '-' : row.passing_score
        const totalScore = row.question_score * this.getQuestionCount(row)
        if (totalScore) {
          return totalScore ? `${passingScore}/${totalScore}` : passingScore
        } else {
          return '-'
        }
      } else {
        const statusItem = (this.examOptions.openBookMaps || []).find(item => item.value === row.open_book_exam)
        return statusItem ? statusItem.label : ''
      }
    },
    getActionConfig (name) {
      const actions = getExamStatusActions(this.consts.STATUS_NEW)
        .concat(getExamStatusActions(this.consts.STATUS_ONLINE))
        .concat(getExamStatusActions(this.consts.STATUS_OFFLINE))
        .concat(getExamStatusActions(this.consts.STATUS_BANNED))
      return actions.find(action => action.name === name)
    },
    handleExamAction (row, name) {
      const action = this.getActionConfig(name)
      if (!action) {
        return
      }
      this.$confirm(`确定要${action.label}考试【${row.title}】吗？`, '提示', {type: 'warning'}).then(() => {
        request.common(action.apiName, row.id).then(() => {
          this.$message.success(action.successText)
          this.onRefresh()
        })
      })
    },
    getExamRecordLink (row) {
      return {
        path: `/training/examination/${row.id}/intern/records`,
        query: { title: row.title },
      }
    },
  },
}
</script>

<style lang="scss"
       scoped>
.exam-title {
  display: inline-flex;
  align-items: center;
  line-height: 20px;
  color: #1f2d3d;
}

.knowledge-name {
  display: inline-flex;
  align-items: center;
  line-height: 20px;
  color: #1f2d3d;
}

.knowledge-name__icon {
  margin-right: 8px;
  color: #f5a623;
  font-size: 14px;
}

.exam-paper-count {
  color: #005cff;
}

.exam-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: 22px;
  padding: 0 12px;
  border-radius: 11px;
  box-sizing: border-box;
  font-size: 12px;
  line-height: 22px;
  white-space: nowrap;
}

.exam-tag--primary {
  color: #409EFF;
  background-color: #ecf5ff;
}

.exam-tag--success {
  color: #00a854;
  background-color: #d9f7e7;
}

.exam-tag--warning {
  color: #e6a23c;
  background-color: #fdf6ec;
}

.exam-tag--danger {
  color: #f56c6c;
  background-color: #fef0f0;
}

.exam-tag--info,
.exam-tag--default {
  color: #1f2d3d;
  background-color: #f4f5f7;
}
</style>



// WEBPACK FOOTER //
// src/views/training/pages/exam/InternTrainingExaminationTable.vue